import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../utils/setupAxios';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 500;

const formatDate = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? String(ts) : d.toLocaleDateString();
};

const isValidImgUrl = (url) => typeof url === 'string' && url.trim() !== '' && !/\.html?$/i.test(url);

// Build a human readable price range from the antique reference price fields.
const formatPriceRange = (item) => {
  const min = item?.reference_price_min;
  const max = item?.reference_price_max;
  if (min != null && min !== '' && max != null && max !== '') return `$${min} - $${max}`;
  if (item?.reference_price) return String(item.reference_price);
  if (min != null && min !== '') return `$${min}`;
  return null;
};

const emptyFilters = {
  categories: [],
  conditions: [],
  rarities: [],
};

export default function CardsList() {
  const [searchInput, setSearchInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [conditionInput, setConditionInput] = useState('');
  const [rarityInput, setRarityInput] = useState('');

  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState(emptyFilters);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, items_per_page: PAGE_SIZE };
      if (query) params.q = query;
      if (categoryFilter) params.category = categoryFilter;
      if (conditionFilter) params.condition = conditionFilter;
      if (rarityFilter) params.rarity = rarityFilter;

      const response = await axiosInstance.get('/era/admin/items', { params });
      if (response.data?.success) {
        const data = response.data.data || {};
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(Number(data.objects?.total ?? 0));
      } else {
        setError(response.data?.message || 'Failed to load antiques');
        setItems([]);
        setTotal(0);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load antiques');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, query, categoryFilter, conditionFilter, rarityFilter]);

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/era/admin/items/filters')
      .then((response) => {
        if (cancelled) return;
        const d = response.data?.data || {};
        setFilterOptions({
          categories: Array.isArray(d.categories) ? d.categories : [],
          conditions: Array.isArray(d.conditions) ? d.conditions : [],
          rarities:   Array.isArray(d.rarities) ? d.rarities : [],
        });
      })
      .catch(() => { if (!cancelled) setFilterOptions(emptyFilters); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setQuery(searchInput.trim());
      setCategoryFilter(categoryInput);
      setConditionFilter(conditionInput);
      setRarityFilter(rarityInput);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput, categoryInput, conditionInput, rarityInput]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleClear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    setCategoryInput('');
    setConditionInput('');
    setRarityInput('');
    setQuery('');
    setCategoryFilter('');
    setConditionFilter('');
    setRarityFilter('');
    setPage(1);
  };

  const hasFilters = searchInput || categoryInput || conditionInput || rarityInput;

  const pageNumbers = () => {
    const start = Math.max(1, Math.min(page - 2, lastPage - 4));
    const end = Math.min(lastPage, start + 4);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const PaginationBar = () => (
    <div className="d-flex align-items-center px-3 py-2">
      <p className="m-0 text-muted small">
        Page <strong>{page}</strong> / <strong>{lastPage}</strong>
        {' · '}{(total ?? 0).toLocaleString()} total
      </p>
      {lastPage > 1 && (
        <ul className="pagination pagination-sm m-0 ms-auto">
          <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
          </li>
          {pageNumbers().map((n) => (
            <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setPage(n)}>{n}</button>
            </li>
          ))}
          <li className={`page-item ${page >= lastPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>›</button>
          </li>
        </ul>
      )}
    </div>
  );

  const renderMobileCard = (item) => {
    const thumb = isValidImgUrl(item.image_url) ? item.image_url : null;
    const price = formatPriceRange(item);
    return (
      <MobileCard key={item.item_id}>
        <MobileCardTop>
          {thumb ? (
            <img
              src={thumb}
              alt={item.name}
              style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6', flexShrink: 0 }}
            />
          ) : null}
          <MobileCardTitle>{item.name || '-'}</MobileCardTitle>
        </MobileCardTop>
        <MobileCardRow label="Item ID" mono>{item.item_id}</MobileCardRow>
        <MobileCardRow label="Category">{item.category || '-'}</MobileCardRow>
        <MobileCardRow label="Price">{price || '-'}</MobileCardRow>
        <MobileCardRow label="Condition">{item.condition || '-'}</MobileCardRow>
        <MobileCardRow label="Rarity">{item.rarity || '-'}</MobileCardRow>
        <MobileCardRow label="Origin">{item.origin || '-'}</MobileCardRow>
        <MobileCardRow label="Period">{item.time_period || '-'}</MobileCardRow>
        <MobileCardRow label="Added">{formatDate(item.created_at)}</MobileCardRow>
        <MobileCardFooter>
          <button className="btn btn-sm btn-outline-primary" onClick={() => setSelected(item)}>
            View
          </button>
        </MobileCardFooter>
      </MobileCard>
    );
  };

  return (
    <div className="dashboard-list-page">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title">Antique List</h2>
            {!loading && (
              <div className="text-muted mt-1">
                {(total ?? 0).toLocaleString()} antiques in database
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-sm-4">
              <label className="form-label mb-1">Name / Origin / Item ID</label>
              <input
                className="form-control"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Terracotta vase, Mediterranean, 2147…"
              />
            </div>
            <div className="col-12 col-sm-3">
              <label className="form-label mb-1">Category</label>
              <select className="form-select" value={categoryInput} onChange={(e) => setCategoryInput(e.target.value)}>
                <option value="">All categories</option>
                {filterOptions.categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Condition</label>
              <select className="form-select" value={conditionInput} onChange={(e) => setConditionInput(e.target.value)}>
                <option value="">All conditions</option>
                {filterOptions.conditions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="form-label mb-1">Rarity</label>
              <select className="form-select" value={rarityInput} onChange={(e) => setRarityInput(e.target.value)}>
                <option value="">All rarities</option>
                {filterOptions.rarities.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            {hasFilters && (
              <div className="col-auto">
                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
                  Clear filter
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {error && <div className="alert alert-danger m-3 mb-0">{error}</div>}

        {!loading && total > 0 && <PaginationBar />}

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-muted py-5">No antiques found.</div>
        ) : (
          <>
            <ListDesktop>
              <table className="table table-vcenter table-hover card-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Image</th>
                    <th style={{ width: 150 }}>Item ID</th>
                    <th>Name</th>
                    <th style={{ width: 140 }}>Category</th>
                    <th style={{ width: 130 }}>Price</th>
                    <th style={{ width: 120 }}>Condition</th>
                    <th style={{ width: 120 }}>Rarity</th>
                    <th style={{ width: 170 }}>Origin</th>
                    <th style={{ width: 110 }}>Added</th>
                    <th style={{ width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const thumb = isValidImgUrl(item.image_url) ? item.image_url : null;
                    const price = formatPriceRange(item);
                    return (
                      <tr key={item.item_id}>
                        <td>
                          {thumb ? (
                            <img
                              src={thumb}
                              alt={item.name}
                              style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                            />
                          ) : (
                            <div style={{ width: 48, height: 48, background: '#f1f3f4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: 18 }}>
                              ○
                            </div>
                          )}
                        </td>
                        <td><code className="small">{item.item_id}</code></td>
                        <td>
                          <div className="fw-semibold">{item.name || '-'}</div>
                          {item.materials && <div className="text-muted small">{item.materials}</div>}
                        </td>
                        <td>
                          {item.category ? (
                            <span className="badge bg-blue-lt text-blue" style={{ fontWeight: 500 }}>
                              {item.category}
                            </span>
                          ) : <span className="text-muted">-</span>}
                        </td>
                        <td className="small fw-semibold">{price || <span className="text-muted fw-normal">-</span>}</td>
                        <td>
                          {item.condition ? (
                            <span className="badge bg-green-lt text-green" style={{ fontSize: '0.7rem' }}>
                              {item.condition}
                            </span>
                          ) : <span className="text-muted">-</span>}
                        </td>
                        <td>
                          {item.rarity ? (
                            <span className="badge bg-orange-lt text-orange" style={{ fontSize: '0.7rem' }}>
                              {item.rarity}
                            </span>
                          ) : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-muted small">{item.origin || '-'}</td>
                        <td className="text-muted small">{formatDate(item.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setSelected(item)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ListDesktop>

            <ListMobile>
              {items.map((item) => renderMobileCard(item))}
            </ListMobile>
          </>
        )}

        {!loading && total > 0 && (
          <div className="card-footer p-0">
            <PaginationBar />
          </div>
        )}
      </div>

      {selected && (
        <AntiqueDetailModal itemId={selected.item_id} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function AntiqueDetailModal({ itemId, onClose }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => { document.body.classList.remove('modal-open'); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance.get(`/era/admin/items/${itemId}`)
      .then((response) => {
        if (cancelled) return;
        if (response.data?.success) {
          setItem(response.data.data);
        } else {
          setError(response.data?.message || 'Failed to load antique');
        }
      })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.message || e.message || 'Failed to load antique'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [itemId]);

  const image = isValidImgUrl(item?.image_url) ? item.image_url : null;
  const price = formatPriceRange(item);
  const visualMatches = Array.isArray(item?.visual_matches) ? item.visual_matches : [];

  return ReactDOM.createPortal(
    <>
      <div className="modal-backdrop fade show" />
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex="-1"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-fullscreen-md-down">
          <div className="modal-content">

            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-1">{item?.name || 'Antique Detail'}</h5>
                <div className="d-flex flex-wrap gap-1">
                  {item?.category && <span className="badge bg-blue-lt text-blue">{item.category}</span>}
                  {item?.condition && <span className="badge bg-green-lt text-green">{item.condition}</span>}
                  {item?.rarity && <span className="badge bg-orange-lt text-orange">{item.rarity}</span>}
                  {item?.materials && <span className="badge bg-secondary-lt text-secondary">{item.materials}</span>}
                </div>
              </div>
              <button type="button" className="btn-close ms-3" onClick={onClose} />
            </div>

            <div className="modal-body">
              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : item ? (
                <>
                  {image && (
                    <div className="mb-4">
                      <div className="text-muted small fw-semibold text-uppercase mb-2">Recognized Image</div>
                      <a href={image} target="_blank" rel="noreferrer" className="d-inline-block" style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #dee2e6' }}>
                        <img
                          src={image}
                          alt={item.name}
                          style={{ width: 220, height: 220, objectFit: 'contain', background: '#f8f9fa', display: 'block' }}
                        />
                      </a>
                    </div>
                  )}

                  <div className="mb-4">
                    <div className="text-muted small fw-semibold text-uppercase mb-2">Overview</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
                      {[
                        { label: 'Item ID', value: item.item_id },
                        { label: 'Session ID', value: item.session_id || '-' },
                        { label: 'Category', value: item.category || '-' },
                        { label: 'Reference Price', value: price || '-' },
                        { label: 'Condition', value: item.condition || '-' },
                        { label: 'Rarity', value: item.rarity || '-' },
                        { label: 'Origin', value: item.origin || '-' },
                        { label: 'Time Period', value: item.time_period || '-' },
                        { label: 'Materials', value: item.materials || '-' },
                        { label: 'Added', value: formatDate(item.created_at) },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ minWidth: 140 }}>
                          <div className="text-muted small">{label}</div>
                          <div className="fw-semibold">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {item.value_insights && (
                    <div className="mb-4">
                      <div className="text-muted small fw-semibold text-uppercase mb-2">Value Insights</div>
                      <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                        {item.value_insights}
                      </div>
                    </div>
                  )}

                  {visualMatches.length > 0 && (
                    <div className="mb-4">
                      <div className="text-muted small fw-semibold text-uppercase mb-2">
                        Visual Matches ({visualMatches.length})
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 50 }}>Image</th>
                              <th style={{ width: 40 }}>#</th>
                              <th>Title</th>
                              <th style={{ width: 150 }}>Source</th>
                              <th style={{ width: 90 }}>Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visualMatches.map((m, i) => {
                              const thumb = isValidImgUrl(m.thumbnail) ? m.thumbnail : null;
                              return (
                                <tr key={m.id || i}>
                                  <td>
                                    {thumb ? (
                                      <img src={thumb} alt={m.title || ''} style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />
                                    ) : <span className="text-muted">-</span>}
                                  </td>
                                  <td className="text-muted small">{m.position ?? '-'}</td>
                                  <td className="small">
                                    {m.link ? (
                                      <a href={m.link} target="_blank" rel="noreferrer">{m.title || m.link}</a>
                                    ) : (m.title || '-')}
                                  </td>
                                  <td className="small text-muted">{m.source || '-'}</td>
                                  <td className="small">{m.price || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
